import variablesData from "../../../Data/cdem_variables.json";

export const yearToPrefix = (year) => {
  const yy = String(year).slice(-2).padStart(2, "0");
  return `dc${yy}`;
};

export const resolveVarIdForStudy = (variableIDList = [], prefix = "") => {
  const exact = variableIDList.find((v) => typeof v === "string" && v.startsWith(`${prefix}_`));
  if (exact) return exact;
  const loose = variableIDList.find((v) => typeof v === "string" && v.startsWith(prefix));
  if (loose) return loose;
  return variableIDList[variableIDList.length - 1] || "";
};

const resolveAnswersMappingForStudy = (item = {}, variableId = "", prefix = "") => {
  const byVariableId = item.answersMappingByVariableID || item.answersMappingByVariableId || {};
  if (variableId && byVariableId[variableId]) return byVariableId[variableId];

  const byPrefix = item.answersMappingByPrefix || {};
  if (prefix && byPrefix[prefix]) return byPrefix[prefix];

  return item.answersMapping || {};
};

const allVariableItems = [
  ...Object.values(variablesData?.Independent || {}).flat(),
  ...Object.values(variablesData?.Dependent || {}).flat(),
];

export const resolveVariableIdForYear = (currentVariableId, year) => {
  const normalizedCurrent = String(currentVariableId || "").trim();
  if (!normalizedCurrent || !year) return "";

  const targetPrefix = yearToPrefix(year);
  if (!targetPrefix) return "";

  const matchedItem = allVariableItems.find((item) =>
    (item?.VariableID || []).includes(normalizedCurrent)
  );
  if (matchedItem) {
    return resolveVarIdForStudy(matchedItem.VariableID || [], targetPrefix);
  }

  return normalizedCurrent.replace(/^dc\d{2}(?=_|$)/i, targetPrefix);
};

export const buildGroupsForPrefix = (sectionObj, prefix) =>
  Object.entries(sectionObj || {})
    .map(([group, items]) => ({
      group,
      options: (items || [])
        .map((item) => ({
          id: resolveVarIdForStudy(item.VariableID, prefix),
          label: item.Label,
          type: item.Type,
          question: item.Label,
        }))
        .filter((option) => !!option.id),
    }))
    .filter((group) => group.options.length);

export const buildQuestionsIndexForPrefix = (prefix) => {
  const index = {};

  Object.values(variablesData?.Independent || {}).forEach((items) => {
    (items || []).forEach((item) => {
      const variableId = resolveVarIdForStudy(item.VariableID, prefix);
      if (!variableId) return;
      index[variableId] = {
        mainQuestion: item.Question || "",
        answersMapping: resolveAnswersMappingForStudy(item, variableId, prefix),
      };
    });
  });

  Object.values(variablesData?.Dependent || {}).forEach((items) => {
    (items || []).forEach((item) => {
      const variableId = resolveVarIdForStudy(item.VariableID, prefix);
      if (!variableId) return;
      index[variableId] = {
        mainQuestion: item.Question || item.Label || "",
        answersMapping: resolveAnswersMappingForStudy(item, variableId, prefix),
      };
    });
  });

  return index;
};

const toNumber = (displayValue) => {
  const parsed = Number(String(displayValue).trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const decadeLabel = (year) => `${Math.floor(year / 10) * 10}s`;

const buildAgeBuckets = (answersMapping) => {
  const pairs = Object.entries(answersMapping)
    .map(([rawId, value]) => [rawId, toNumber(value?.Display)])
    .filter(([, value]) => value !== null && value >= 0 && value <= 115);

  const bins = [
    { id: "<18", label: "<18", start: 0, end: 17 },
    { id: "18-24", label: "18-24", start: 18, end: 24 },
    { id: "25-34", label: "25-34", start: 25, end: 34 },
    { id: "35-44", label: "35-44", start: 35, end: 44 },
    { id: "45-54", label: "45-54", start: 45, end: 54 },
    { id: "55-64", label: "55-64", start: 55, end: 64 },
    { id: "65-74", label: "65-74", start: 65, end: 74 },
    { id: "75+", label: "75+", start: 75, end: 115 },
  ];

  const groupMembers = {};
  const displayByGroupId = {};
  bins.forEach((bin) => {
    groupMembers[bin.id] = [];
    displayByGroupId[bin.id] = bin.label;
  });

  for (const [rawId, age] of pairs) {
    const bin = bins.find((item) => age >= item.start && age <= item.end);
    if (bin) groupMembers[bin.id].push(rawId);
  }

  const filters = bins
    .filter((bin) => groupMembers[bin.id].length)
    .map((bin) => ({ id: bin.id, display: bin.label }));

  return { filters, groupMembers, displayByGroupId };
};

const buildYearBuckets = (answersMapping, yearMin, yearMax, extraLabels = []) => {
  const byYear = [];
  const passthrough = [];

  for (const [rawId, value] of Object.entries(answersMapping)) {
    const display = value?.Display ?? "";
    const parsed = toNumber(display);
    if (parsed !== null && parsed >= yearMin && parsed <= yearMax) {
      byYear.push([rawId, parsed]);
    } else {
      passthrough.push({ rawId, display });
    }
  }

  const firstDecade = Math.floor(yearMin / 10) * 10;
  const lastDecade = Math.floor(yearMax / 10) * 10;

  const decades = [];
  for (let decade = firstDecade; decade <= lastDecade; decade += 10) {
    decades.push({ id: `${decade}s`, label: decadeLabel(decade) });
  }

  const groupMembers = {};
  const displayByGroupId = {};
  decades.forEach((decade) => {
    groupMembers[decade.id] = [];
    displayByGroupId[decade.id] = decade.label;
  });

  for (const [rawId, year] of byYear) {
    const key = `${Math.floor(year / 10) * 10}s`;
    if (groupMembers[key]) groupMembers[key].push(rawId);
  }

  const decadeFilters = decades
    .filter((decade) => groupMembers[decade.id].length)
    .map((decade) => ({ id: decade.id, display: decade.label }));

  const passthroughFilters = [];
  for (const passthroughValue of passthrough) {
    if (
      extraLabels.length === 0 ||
      extraLabels.some(
        (allowed) =>
          String(allowed.display).toLowerCase() === String(passthroughValue.display).toLowerCase()
      )
    ) {
      groupMembers[passthroughValue.display] = [passthroughValue.rawId];
      displayByGroupId[passthroughValue.display] = passthroughValue.display;
      passthroughFilters.push({ id: passthroughValue.display, display: passthroughValue.display });
    }
  }

  return {
    filters: [...decadeFilters, ...passthroughFilters],
    groupMembers,
    displayByGroupId,
  };
};

export const maybeBuildGroupedFilters = (independentMeta) => {
  if (!independentMeta) return { grouped: false };

  const question = independentMeta.mainQuestion?.toLowerCase() || "";
  const answersMapping = independentMeta.answersMapping || {};

  if (question.includes("how old are you")) {
    return { grouped: true, kind: "age", ...buildAgeBuckets(answersMapping) };
  }

  if (
    question.includes("what year did you come to live in canada") ||
    question.includes("come to live in canada")
  ) {
    return {
      grouped: true,
      kind: "immig",
      ...buildYearBuckets(answersMapping, 1920, 2020, [
        { display: "Don't know/ Prefer not to answer" },
      ]),
    };
  }

  if (
    question.includes("what year were you born") ||
    question.includes("in what year were you born")
  ) {
    return { grouped: true, kind: "birth", ...buildYearBuckets(answersMapping, 1920, 2010) };
  }

  const displays = Object.values(answersMapping).map((value) => value?.Display);
  const years = displays
    .map(toNumber)
    .filter((year) => year !== null && year >= 1900 && year <= 2100);
  if (years.length >= 10) {
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);
    return {
      grouped: true,
      kind: "yearish",
      ...buildYearBuckets(answersMapping, minYear, maxYear),
    };
  }

  return { grouped: false };
};

export const isExceptionVar = (variableId) => {
  const id = String(variableId || "");
  return /_pos_no_differen\b/.test(id) || /_democratic_sat\b/.test(id);
};

export const detectNumericScale = (codes = []) => {
  const min = Math.min(...codes);
  const max = Math.max(...codes);

  if (min === 0 && (max === 101 || max === 100)) return "0-100";
  if (min === 0 && (max === 9 || max === 10)) return "0-10";
  return "generic";
};
